import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Checkbox,
  Divider,
  Flex,
  Grid,
  Group,
  ScrollArea,
  TextInput,
  Tooltip,
  Text,
} from "@mantine/core";
import QuestionEditor from "./QuestionEditor";
import { useForm } from "@mantine/form";
import { randomId } from "@mantine/hooks";
import {
  IconCheck,
  IconGripVertical,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import React, { useEffect, useRef, useState } from "react";
import { NotificationExtension } from "../../_base/extension/NotificationExtension";
import { createFAQ } from "../../api/ApiConfigWeb";
import { FAQSubmit } from "../../model/FAQModel";

const CreateFAQ = ({
  id,
  onSuccess,
  onClose,
}: {
  id: number;
  onSuccess: any;
  onClose: any;
}) => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);
  const [urlChange, setUrlChange] = useState<string>("");
  const [title, setTitle] = useState<string>("");

  const viewport = useRef<HTMLDivElement>(null);

  const form = useForm<FAQSubmit>({
    mode: "uncontrolled",
    initialValues: {
      dataSubmit: [
        {
          id: 0,
          clientId: randomId(),
          question: "",
          answer: "",
          active: true,
          isDeleted: false,
          createdDate: null,
          lastUpdatedDate: null,
          createdBy: null,
          lastUpdatedBy: null,
          attribute1: null,
          attribute2: null,
          attribute3: null,
          attribute4: null,
          attribute5: null,
          attribute6: null,
          attribute7: null,
          attribute8: null,
          attribute9: null,
          attribute10: null,
        },
      ],
    },
  });

  const fields = form
    .getValues()
    .dataSubmit.map((item: any, actualIndex: number) => {
      if (item.isDeleted) return null;

      const visibleIndex = form
        .getValues()
        .dataSubmit.filter(
          (it: any, idx: number) => !it.isDeleted && idx < actualIndex,
        ).length;

      return (
        <Draggable
          key={item.clientId ?? String(item.id)}
          index={visibleIndex}
          draggableId={item.clientId ?? String(item.id)}
        >
          {(provided) => (
            <Group
              flex={"col"}
              ref={provided.innerRef}
              {...provided.draggableProps}
            >
              <Divider
                label={
                  <Badge size="lg" variant="filled" color="blue">
                    CÂU HỎI SỐ {visibleIndex + 1}
                  </Badge>
                }
                labelPosition="center"
                w={"100%"}
                mb={-10}
                mt={10}
              />
              <Flex w={"100%"} gap={"xs"}>
                <Center {...provided.dragHandleProps}>
                  <IconGripVertical size={18} />
                </Center>
                <Flex direction={"column"} w={"92.5%"}>
                  <Grid>
                    <Grid.Col span={12}>
                      <TextInput
                        key={form.key(`dataSubmit.${actualIndex}.question`)}
                        label="Nội dung câu hỏi"
                        placeholder="Ví dụ: Sản phẩm này có bảo hành bao lâu? Tôi có thể đổi trả trong bao lâu?"
                        withAsterisk
                        size="md"
                        {...form.getInputProps(
                          `dataSubmit.${actualIndex}.question`,
                        )}
                      />
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Box>
                        <Text fw={600} size="sm" mb={5}>
                          Nội dung câu trả lời{" "}
                          <span style={{ color: "red" }}>*</span>
                        </Text>
                        <QuestionEditor
                          form={form}
                          index={actualIndex}
                          itemId={item.clientId ?? item.id}
                        />
                      </Box>
                    </Grid.Col>
                    <Grid.Col span={12}>
                      <Flex
                        justify={"space-between"}
                        align={"center"}
                        w={"100%"}
                      >
                        <Checkbox
                          mt={5}
                          label="Hiển thị câu hỏi này"
                          description="Bỏ tick để tạm ẩn câu hỏi khỏi website"
                          size="sm"
                          key={form.key(`dataSubmit.${actualIndex}.active`)}
                          {...form.getInputProps(
                            `dataSubmit.${actualIndex}.active`,
                            {
                              type: "checkbox",
                            },
                          )}
                        />
                        {form
                          .getValues()
                          .dataSubmit.filter((it: any) => !it.isDeleted)
                          .length > 1 ? (
                          <Tooltip
                            label="Xóa câu hỏi này khỏi danh sách"
                            position="left"
                          >
                            <ActionIcon
                              variant="filled"
                              color="red"
                              size="lg"
                              onClick={() => {
                                form.setFieldValue(
                                  `dataSubmit.${actualIndex}.isDeleted`,
                                  true,
                                );
                              }}
                            >
                              <IconTrash size={18} stroke={1.5} />
                            </ActionIcon>
                          </Tooltip>
                        ) : (
                          <></>
                        )}
                      </Flex>
                    </Grid.Col>
                  </Grid>
                </Flex>
              </Flex>
            </Group>
          )}
        </Draggable>
      );
    })
    .filter(Boolean);

  const scrollToTop = () =>
    viewport.current!.scrollTo({ top: 0, behavior: "smooth" });

  const handleCreateFAQ = async (dataSubmit: any) => {
    // Chỉ validate các items chưa bị xóa
    const activeItems = dataSubmit.filter((item: any) => !item.isDeleted);
    if (
      activeItems.some(
        (item: any) =>
          !item.question ||
          String(item.question).length === 0 ||
          !item.answer ||
          String(item.answer).length === 0,
      )
    ) {
      NotificationExtension.Fails(
        "Vui lòng nhập đầy đủ nội dung câu hỏi và câu trả lời cho tất cả các mục!",
      );
      return;
    } else if (urlChange.length === 0) {
      NotificationExtension.Fails("Vui lòng nhập đường dẫn URL!");
    } else if (title.length === 0) {
      NotificationExtension.Fails("Vui lòng nhập tiêu đề nhóm câu hỏi!");
    } else {
      // Với Create, chỉ gửi các items chưa bị xóa (vì chưa tồn tại trong DB)
      const activeItems = dataSubmit.filter((item: any) => !item.isDeleted);
      const dataModel = activeItems.map((item: any) => {
        const { clientId, ...rest } = item;
        return {
          ...rest,
          id: 0,
          active: item.active ? "Y" : "N",
        };
      });

      await createFAQ(
        {
          idCategory: id,
          // url: listUrl.join(","),
          url: !urlChange.startsWith("/") ? "/" + urlChange : urlChange,
          title: title.trim(),
          tblFaqQaCommands: dataModel,
        },
        onSuccess,
        onClose,
      );
    }
  };

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (450 + headerHeight));
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [height]);

  return (
    <Box
      component="form"
      onSubmit={form.onSubmit((e: FAQSubmit) => handleCreateFAQ(e.dataSubmit))}
    >
      <Grid w={"96.5%"}>
        <Grid.Col span={6}>
          <TextInput
            label="Đường dẫn URL"
            description="Nhập đường dẫn trang web mà FAQ này sẽ được hiển thị. Bắt đầu bằng dấu /"
            placeholder="Ví dụ: /laptop, /laptop-dell-inspiron-14, /may-tinh-xach-tay"
            withAsterisk
            size="md"
            value={urlChange}
            onChange={(e) => setUrlChange(e.currentTarget.value)}
          />
        </Grid.Col>
        <Grid.Col span={6}>
          <TextInput
            label="Tiêu đề nhóm câu hỏi"
            description="Tiêu đề mô tả chủ đề chung của các câu hỏi thường gặp này"
            placeholder="Ví dụ: Câu hỏi về sản phẩm, Chính sách bảo hành, Hướng dẫn mua hàng"
            withAsterisk
            size="md"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
          />
        </Grid.Col>
      </Grid>
      <ScrollArea h={height + 130} viewportRef={viewport} mt={5}>
        <DragDropContext
          onDragEnd={({ destination, source }) =>
            destination?.index !== undefined &&
            form.reorderListItem("dataSubmit", {
              from: source.index,
              to: destination.index,
            })
          }
        >
          <Droppable droppableId="dnd-list" direction="vertical">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {fields}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </ScrollArea>

      <Group justify="center" mt="xl" gap="lg">
        <Button
          leftSection={<IconPlus size={18} />}
          color="blue"
          size="lg"
          variant="light"
          onClick={() => {
            form.insertListItem(
              "dataSubmit",
              {
                id: 0,
                clientId: randomId(),
                question: "",
                answer: "",
                active: true,
                isDeleted: false,
                createdDate: null,
                lastUpdatedDate: null,
                createdBy: null,
                lastUpdatedBy: null,
                attribute1: null,
                attribute2: null,
                attribute3: null,
                attribute4: null,
                attribute5: null,
                attribute6: null,
                attribute7: null,
                attribute8: null,
                attribute9: null,
                attribute10: null,
              },
              form.getValues().dataSubmit.length,
            );
          }}
        >
          Thêm câu hỏi mới
        </Button>
        <Button
          leftSection={<IconCheck size={18} />}
          color="teal"
          size="lg"
          type="submit"
        >
          Lưu tất cả thay đổi
        </Button>
      </Group>
    </Box>
  );
};

export default CreateFAQ;
